import React, { useState, useEffect, useRef } from 'react';
import { getPlayerEquipment, equipGearToCharacterSheet, unequipGear } from '../../DatabaseAPI';
import PlayerImagePanel from '../player/PlayerImagePanel';
import PlayerEquipmentPanel from '../player/PlayerEquipmentPanel';
import PlayerScorePanel from '../player/PlayerScorePanel';
import GearSelectionPanel from '../player/GearSelectionPanel';

function ExpandablePlayerCard({
    player,
    isExpanded,
    viewMode,
    onExpand,
    onScoreClick,
    onGearClick,
    teamId,
    leagueId,
    allTeamPlayers = [],
    isOpponentView = false,
    canModifyGear = true,
    weekStatus = null,
    teamGear = [], // new prop
    onGearChange // new prop
}) {
    const [playerEquipment, setPlayerEquipment] = useState([]);
    const [selectedGear, setSelectedGear] = useState(null);
    const [loadingEquipment, setLoadingEquipment] = useState(false);
    const [expandedState, setExpandedState] = useState('collapsed'); // 'collapsed', 'equipment', 'score', 'gear'
    const [previousState, setPreviousState] = useState('collapsed'); // Track the previous state before gear selection
    const [equippedGear, setEquippedGear] = useState(new Set()); // Track which equipped gear are equipped
    const [nameScale, setNameScale] = useState(1); // Dynamic scaling for player name
    const [screenSize, setScreenSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    const [gearActionLoading, setGearActionLoading] = useState({}); // Track loading state for each gear
    const nameRef = useRef(null);

    // Track screen size changes
    useEffect(() => {
        const handleResize = () => {
            setScreenSize({ width: window.innerWidth, height: window.innerHeight });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Calculate if we're on mobile
    const isMobile = screenSize.width <= 768;

    // Calculate name scaling based on text width and screen size
    useEffect(() => {
        if (nameRef.current && player.name) {
            const element = nameRef.current;
            const maxWidth = isMobile ? 140 : 200; // More space on desktop

            // Reset scale to measure natural width
            element.style.transform = 'scale(1)';
            const naturalWidth = element.scrollWidth;

            if (naturalWidth > maxWidth) {
                const scale = maxWidth / naturalWidth;
                setNameScale(Math.max(scale, 0.6)); // Minimum scale of 0.6 to keep text readable
            } else {
                setNameScale(1);
            }
        }
    }, [player.name, isMobile]);

    // Load player's equipped gear when needed
    useEffect(() => {
        // Use the fresh character sheet ID from team data, with fallback to player.id
        const freshPlayerData = allTeamPlayers.find(p => p.name === player.name);
        const characterSheetId = freshPlayerData?.character_sheet_id || player.character_sheet_id || player.id;
        console.log(`🔧 Character sheet ID resolution for ${player.name}:`, {
            freshData: freshPlayerData?.character_sheet_id,
            playerCharSheet: player.character_sheet_id,
            playerId: player.id,
            using: characterSheetId
        });

        if ((viewMode === 'gear' || expandedState !== 'collapsed') && characterSheetId) {
            setLoadingEquipment(true);
            getPlayerEquipment(characterSheetId)
                .then(equipmentData => {
                    console.log('Player equipment data:', equipmentData);
                    setPlayerEquipment(equipmentData?.equipment || {});
                })
                .catch(error => {
                    console.error('Error loading player equipment for characterSheetId:', characterSheetId, error);
                    setPlayerEquipment({});
                })
                .finally(() => setLoadingEquipment(false));
        }
    }, [viewMode, expandedState, player.character_sheet_id, player.id, player.name, allTeamPlayers]);

    const handleGearEquip = async (gear) => {
        setGearActionLoading(prev => ({ ...prev, [gear.id]: true }));
        try {
            // Use the fresh character sheet ID from team data, with fallback to player.id
            const freshPlayerData = allTeamPlayers.find(p => p.name === player.name);
            const characterSheetId = freshPlayerData?.character_sheet_id || player.character_sheet_id || player.id;
            console.log(`🔧 Equip - Character sheet ID resolution for ${player.name}:`, {
                freshData: freshPlayerData?.character_sheet_id,
                playerCharSheet: player.character_sheet_id,
                playerId: player.id,
                using: characterSheetId
            });

            if (!characterSheetId) {
                console.error('No character sheet ID found for player:', player);
                return;
            }
            
            console.log('=== GEAR EQUIP DEBUG ===');
            console.log('Player:', player.name, 'CharacterSheetId:', characterSheetId);
            console.log('Gear to equip:', gear);
            console.log('Gear ID:', gear.id, 'Gear Name:', gear.name);
            console.log('TeamId for loading gear:', teamId);
            
            console.log('Calling equipGearToCharacterSheet with gear.id:', gear.id, 'characterSheetId:', characterSheetId);
            await equipGearToCharacterSheet(gear.id, characterSheetId);
            console.log('✅ Successfully equipped gear');
            
            // Refresh player equipment
            const updatedPlayerEquipment = await getPlayerEquipment(characterSheetId);
            setPlayerEquipment(updatedPlayerEquipment?.equipment || {});
            setSelectedGear(null);
            if (onGearChange) await onGearChange(); // notify parent to refresh teamGear for all cards
        } catch (error) {
            console.error('❌ Error equipping gear:', error);
            alert(`Failed to equip gear "${gear.name}": ${error.message}`);
            return; // Don't refresh lists on error
        } finally {
            setGearActionLoading(prev => ({ ...prev, [gear.id]: false }));
        }
    };

    const handleGearUnequip = async (gearId) => {
        setGearActionLoading(prev => ({ ...prev, [gearId]: true }));
        try {
            // Use the fresh character sheet ID from team data, with fallback to player.id
            const freshPlayerData = allTeamPlayers.find(p => p.name === player.name);
            const characterSheetId = freshPlayerData?.character_sheet_id || player.character_sheet_id || player.id;
            console.log(`🔧 Unequip - Character sheet ID resolution for ${player.name}:`, {
                freshData: freshPlayerData?.character_sheet_id,
                playerCharSheet: player.character_sheet_id,
                playerId: player.id,
                using: characterSheetId
            });

            if (!characterSheetId) {
                console.error('No character sheet ID found for player:', player);
                return;
            }
            
            await unequipGear(gearId);
            // Refresh player equipment
            const updatedPlayerEquipment = await getPlayerEquipment(characterSheetId);
            setPlayerEquipment(updatedPlayerEquipment?.equipment || {});
            if (onGearChange) await onGearChange(); // notify parent to refresh teamGear for all cards
        } catch (error) {
            console.error('Error unequipping gear:', error);
        } finally {
            setGearActionLoading(prev => ({ ...prev, [gearId]: false }));
        }
    };

    const handleArrowClick = () => {
        console.log('🔽 Arrow clicked! Current state:', expandedState);
        if (expandedState === 'collapsed') {
            setExpandedState('equipment');
            console.log('🔽 Expanding to equipment view');
        } else {
            setExpandedState('collapsed');
            console.log('🔽 Collapsing');
        }
    };

    const handleScoreClick = () => {
        console.log('📊 Score clicked! Current state:', expandedState);
        if (expandedState === 'score') {
            setExpandedState('collapsed');
        } else {
            setExpandedState('score');
        }
    };

    const handleGearClick = (gearId) => {
        if (canModifyGear && !isOpponentView) {
            // Unequip the gear piece
            handleGearUnequip(gearId);
        }
        // Do nothing for opponent view
    };

    const handleAddGearClick = () => {
        if (canModifyGear) {
            setPreviousState(expandedState);
            setSelectedGear(null);
            setExpandedState('gear');
        }
        // Do nothing for opponent view
    };
    const handleCloseLeftPanel = () => {
        setExpandedState('collapsed');
        setSelectedGear(null);
        };
    // Calculate responsive dimensions
    const getCardDimensions = () => {
        if (isMobile) {
            return {
                width: '384px',
                collapsedHeight: '110px',
                expandedHeight: '550px'
            };
        } else {
            return {
                width: Math.min(600, screenSize.width * 0.8) + 'px',
                collapsedHeight: Math.min(150, screenSize.height * 0.2) + 'px',
                expandedHeight: Math.min(700, screenSize.height * 0.8) + 'px'
            };
        }
    };

    const dimensions = getCardDimensions();

    // Calculate dynamic height based on expanded state
    const getCardHeight = () => {
        if (expandedState === 'collapsed') return dimensions.collapsedHeight;
        return dimensions.expandedHeight;
    };

    // Calculate player image dimensions
    const getPlayerImageDimensions = () => {
        const cardWidth = parseInt(dimensions.width);
        const imageWidth = Math.floor(cardWidth * 0.5); // 50% of card width
        const height = expandedState === 'collapsed' ? dimensions.collapsedHeight : dimensions.expandedHeight;

        return {
            width: imageWidth + 'px',
            height: height
        };
    };

    const imageDimensions = getPlayerImageDimensions();

    const cardStyle = {
        width: dimensions.width,
        maxWidth: '600px',
        height: getCardHeight(),
        position: 'relative',
        margin: isMobile ? '10px auto' : '15px auto',
        backgroundColor: '#2a2a2a', // Dark gray instead of black
        overflow: 'hidden',
        transition: 'height 0.3s ease-in-out',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
    };

    // Helper function to get cartoon image path (same as draft system) - ERROR LOUDLY if no GSIS ID
    const getPlayerImagePath = (player) => {
        console.log('� DEBUGGING PLAYER OBJECT FOR:', player.name);
        console.log('� ALL PLAYER FIELDS:', Object.keys(player));
        console.log('� FULL PLAYER OBJECT:', player);
        
        // Check if we have GSIS ID fields available (same logic as draft system)
        const gsisId = player.gsis_id || player.source_player_id;
        
        if (!gsisId) {
            console.error('💥💥💥 ERROR: NO GSIS ID FOUND FOR PLAYER:', player.name);
            console.error('💥💥💥 PLAYER OBJECT:', player);
            console.error('💥💥💥 AVAILABLE FIELDS:', Object.keys(player));
            throw new Error(`MISSING GSIS ID FOR PLAYER: ${player.name}. Cannot use cartoon images without GSIS ID!`);
        }
        
        console.log('✅ USING CARTOON IMAGE FOR:', player.name, 'WITH GSIS ID:', gsisId);
        return `/art/player_images_gsis_cartoon/${gsisId}.png`;
    };

    // Player image section - left side, stretches vertically when expanded
    const playerImageStyle = {
        position: 'absolute',
        left: '0px',
        top: '0px',
        width: imageDimensions.width,
        height: imageDimensions.height,
        backgroundImage: `url(${getPlayerImagePath(player)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
        cursor: 'pointer',
        transition: 'height 0.3s ease-in-out',
        borderRadius: '8px 0 0 8px'
    };

    // Calculate responsive positioning for UI elements
    const getUIElementPositions = () => {
        const cardWidth = parseInt(dimensions.width);
        const imageWidth = parseInt(imageDimensions.width);
        const rightSectionWidth = cardWidth - imageWidth;
        const collapsedHeight = parseInt(dimensions.collapsedHeight);

        return {
            nameSection: {
                width: rightSectionWidth + 'px',
                height: Math.floor(collapsedHeight * 0.55) + 'px'
            },
            pointsSection: {
                width: rightSectionWidth + 'px',
                height: Math.floor(collapsedHeight * 0.45) + 'px'
            },
            rightSectionLeft: imageWidth + 'px'
        };
    };

    const uiPositions = getUIElementPositions();

    // Name section background - top right
    const nameBackgroundStyle = {
        position: 'absolute',
        top: '0px',
        right: '0px',
        width: uiPositions.nameSection.width,
        height: uiPositions.nameSection.height,
        backgroundImage: 'url(/art/nameTextBackground.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
    };

    // Points and arrow section background - bottom right
    const pointsBackgroundStyle = {
        position: 'absolute',
        bottom: '0px',
        right: '0px',
        width: uiPositions.pointsSection.width,
        height: uiPositions.pointsSection.height,
        backgroundImage: 'url(/art/pointAndArrow.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
    };

    // Position text - bottom left corner, responsive
    const positionStyle = {
        position: 'absolute',
        bottom: isMobile ? '5px' : '8px',
        left: isMobile ? '18px' : '25px',
        color: 'white',
        fontFamily: '"Bebas Neue", sans-serif',
        fontSize: isMobile ? '40px' : Math.min(50, screenSize.width * 0.04) + 'px',
        fontWeight: 'bold',
        letterSpacing: '1px',
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
        zIndex: 2
    };

    // Player name - positioned over name background, responsive and adjusted
    const playerNameStyle = {
        position: 'absolute',
        top: isMobile ? '18px' : '24px', // Moved down more
        right: isMobile ? '25px' : '50px', // Moved left more
        width: isMobile ? '140px' : '200px', // Increased width
        color: '#ff4444',
        textAlign: 'center',
        fontFamily: '"Bebas Neue", sans-serif',
        fontSize: isMobile ? '20px' : Math.min(28, screenSize.width * 0.025) + 'px', // Increased size
        fontWeight: 'bold',
        letterSpacing: '1px',
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
        zIndex: 3,
        transform: `scale(${nameScale})`,
        transformOrigin: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden'
    };

    // Score text - positioned over points background, responsive
    const scoreStyle = {
        position: 'absolute',
        bottom: isMobile ? '2px' : '5px',
        left: uiPositions.rightSectionLeft,
        marginLeft: isMobile ? '8px' : '15px',
        color: '#27ae60',
        fontFamily: '"Bebas Neue", sans-serif',
        fontSize: isMobile ? '35px' : Math.min(42, screenSize.width * 0.035) + 'px',
        fontWeight: 'bold',
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
        letterSpacing: '2px',
        zIndex: 3,
        cursor: 'pointer'
    };

    // Arrow - positioned over points background, responsive
    const arrowStyle = {
        position: 'absolute',
        bottom: isMobile ? '-5px' : '-2px',
        right: isMobile ? '10px' : '15px',
        color: 'rgb(0, 120, 215)',
        textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
        zIndex: 3,
        cursor: 'pointer',
        fontSize: isMobile ? '42px' : Math.min(50, screenSize.width * 0.04) + 'px',
        fontFamily: '"Bebas Neue", sans-serif'
    };

    // Get expanded content style based on state - different widths for different modes
    const getExpandedContentStyle = () => {
        const baseStyle = {
            position: 'absolute',
            top: dimensions.collapsedHeight,
            left: '0px',
            bottom: '0px',
            backgroundColor: 'rgba(42, 42, 42, 0.95)',
            padding: isMobile ? '15px' : '20px',
            zIndex: 1,
            borderRadius: '0 0 8px 8px'
        };

        // Equipment-only view - narrower width, positioned to the right
        if (expandedState === 'equipment') {
            return {
                ...baseStyle,
                right: '0px',
                left: 'auto',
                width: isMobile ? '165px' : '260px',
                display: 'block'
            };
        }

        // Full width for score and gear views
        return {
            ...baseStyle,
            right: '0px',
            display: 'flex',
            gap: isMobile ? '10px' : '15px'
        };
    };

    return (
        <div style={cardStyle}>
            {/* Player Image - Left Side (stretches when expanded) */}
            <div
                style={playerImageStyle}
                onClick={onExpand}
            />

            {/* Name Background - Top Right */}
            <div style={nameBackgroundStyle} />

            {/* Points Background - Bottom Right */}
            <div style={pointsBackgroundStyle} />

            {/* Position Text - Bottom Left Corner */}
            <div style={positionStyle}>
                {player.position}
            </div>
            
            {/* Team Matchup Display - More pronounced styling */}
            {player.matchup_display && (
                <div style={{
                    position: 'absolute',
                    bottom: isMobile ? '8px' : '12px',    // Slightly above position baseline
                    left: isMobile ? '75px' : '95px',     // To the right of position text
                    color: '#ffffff',                     // Pure white for visibility
                    fontFamily: '"Bebas Neue", sans-serif', // Same font as position for consistency
                    fontSize: isMobile ? '14px' : '16px', // Larger for visibility
                    fontWeight: '600',                    // Bolder weight
                    letterSpacing: '1px',                 // More spacing like position
                    textShadow: '2px 2px 4px rgba(0,0,0,0.9)', // Stronger shadow
                    zIndex: 2
                }}>
                    {player.matchup_display}
                </div>
            )}

            {/* Player Name - Over Name Background */}
            <div
                ref={nameRef}
                style={playerNameStyle}
            >
                {player.name}
            </div>

            {/* Score Text - Over Points Background - NEW: Show projected points when no actual score yet */}
            <div
                style={scoreStyle}
                onClick={(e) => {
                    e.stopPropagation();
                    handleScoreClick();
                }}
            >
                {player.score && player.score > 0 ?
                    `${player.score}PT` :
                    /* COMMENTED OUT: Projected points display (pre-draft context, not post-draft) */
                    /* player.projected_points ?
                        `Proj: ${Math.round(player.projected_points * 10) / 10}Pts` : */
                        '0PT'
                }
            </div>

            {/* Arrow - Over Points Background */}
            <div
                style={arrowStyle}
                onClick={(e) => {
                    e.stopPropagation();
                    handleArrowClick();
                }}
            >
                {expandedState === 'collapsed' ? '▼' : '▲'}
            </div>

            {/* Expanded Content Area */}
            {expandedState !== 'collapsed' && (
                <div style={getExpandedContentStyle()}>
                    {expandedState === 'equipment' && (
                        <div style={{
                            width: '100%',
                            height: '100%'
                        }}>
                            <h4 style={{
                                margin: '0 0 15px 0',
                                color: '#ff4444',
                                fontFamily: '"Bebas Neue", sans-serif',
                                fontSize: isMobile ? '16px' : '18px',
                                textAlign: 'center'
                            }}>

                            </h4>
                            <PlayerEquipmentPanel
                                playerEquipment={playerEquipment}
                                onGearClick={handleGearClick}
                                onAddGearClick={handleAddGearClick}
                                isOpponentView={isOpponentView}
                                weekStatus={weekStatus}
                                equippedGear={equippedGear}
                                useAbsolutePositioning={false}
                                isMobile={isMobile}
                                isEquipmentOnlyView={true}
                            />
                        </div>
                    )}

                    {expandedState === 'score' && (
                        <>
                            {/* Equipment Summary - Left Side */}
                            <div style={{ flex: 1 }}>
                                <h4 style={{
                                    margin: '0 0 10px 0',
                                    color: '#ff4444',
                                    fontFamily: '"Bebas Neue", sans-serif',
                                    fontSize: isMobile ? '18px' : '20px'
                                }}>
                                    Equipment
                                </h4>
                                <PlayerEquipmentPanel
                                    playerEquipment={playerEquipment}
                                    onGearClick={handleGearClick}
                                    onAddGearClick={handleAddGearClick}
                                    showAddButton={false}
                                    isOpponentView={isOpponentView}
                                    weekStatus={weekStatus}
                                    equippedGear={equippedGear}
                                    useAbsolutePositioning={false}
                                    isMobile={isMobile}
                                />
                            </div>

                            {/* Score Breakdown - Right Side t*/}
                            <div style={{ flex: 1 }}>
                                <h4 style={{
                                    margin: '0 0 10px 0',
                                    color: '#27ae60',
                                    fontFamily: '"Bebas Neue", sans-serif',
                                    fontSize: isMobile ? '18px' : '20px'
                                }}>
                                    Score Breakdown
                                </h4>
                                <PlayerScorePanel player={player} isMobile={isMobile} />
                            </div>
                        </>
                    )}

                    {expandedState === 'gear' && (
                        <>
                            {/* Current Equipment - Left Side */}
                            <div style={{ flex: 1 }}>
                                <h4 style={{
                                    margin: '0 0 10px 0',
                                    color: '#ff4444',
                                    fontFamily: '"Bebas Neue", sans-serif',
                                    fontSize: isMobile ? '18px' : '20px'
                                }}>
                                    Current Equipment
                                </h4>
                                <PlayerEquipmentPanel
                                    playerEquipment={playerEquipment}
                                    onGearClick={handleGearClick}
                                    onAddGearClick={handleAddGearClick}
                                    showAddButton={false}
                                    isOpponentView={isOpponentView}
                                    weekStatus={weekStatus}
                                    equippedGear={equippedGear}
                                    useAbsolutePositioning={false}
                                    isMobile={isMobile}
                                />
                            </div>

                            {/* Available Gear - Right Side */}
                            <div style={{ flex: 1 }}>
                                <h4 style={{
                                    margin: '0 0 10px 0',
                                    color: 'rgb(0, 120, 215)',
                                    fontFamily: '"Bebas Neue", sans-serif',
                                    fontSize: isMobile ? '18px' : '20px'
                                }}>
                                    Available Gear
                                </h4>
                                <GearSelectionPanel
                                    availableGear={teamGear}
                                    selectedGear={selectedGear}
                                    onGearSelect={handleGearEquip}
                                    onGearUnequip={handleGearUnequip}
                                    teamPlayers={allTeamPlayers}
                                    isOpponentView={isOpponentView}
                                    isMobile={isMobile}
                                    onClose={() => {
                                        setExpandedState(previousState);
                                        setSelectedGear(null);
                                    }}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default ExpandablePlayerCard;

